import type { AnnotateCurrentUser, PendingAnnotation, ProjectMember, TestingItemPriority, TestingItemSeverity } from '../types'
import { POPUP_CSS } from './styles'
import { captureElement } from './screenshot'

export interface PopupCreateItemResult {
  title: string
  description?: string
  priority: TestingItemPriority
  severity: TestingItemSeverity
  screenshot?: string
  assignedTo: number | null
}

interface PopupCallbacks {
  onSubmit: (result: PopupCreateItemResult, openAfter: boolean) => void | Promise<void>
  onCancel: () => void
}

function esc(value: string): string {
  return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

export class AnnotationPopup {
  private host: HTMLElement | null = null
  private shadow: ShadowRoot | null = null
  private submitting = false

  showNew(pending: PendingAnnotation, members: ProjectMember[], currentUser: AnnotateCurrentUser | null, callbacks: PopupCallbacks): void {
    this.destroy()
    this.host = document.createElement('div')
    this.host.setAttribute('data-visual-qa', 'popup')
    this.stopHostPropagation(this.host)
    this.shadow = this.host.attachShadow({ mode: 'open' })

    const sheet = new CSSStyleSheet()
    sheet.replaceSync(POPUP_CSS)
    this.shadow.adoptedStyleSheets = [sheet]

    const popup = document.createElement('div')
    popup.className = 'popup'

    const fwBadge = pending.framework ? `<div class="fw-badge">${esc(pending.framework.component)}</div>` : ''
    const selectedText = pending.selectedText
      ? `<div class="selected-text">"${esc(pending.selectedText.slice(0, 120))}"</div>`
      : ''
    const closeIcon = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg>`
    const screenshotIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>`

    let title = ''
    let description = ''
    let screenshot = pending.screenshot ?? null
    let assignedTo: number | null = currentUser?.id ?? null
    let priority: TestingItemPriority = 'medium'
    let severity: TestingItemSeverity = 'minor'

    let submitError = ''
    let setSubmitting = (_active: boolean): void => {}

    const submit = async (openAfter: boolean) => {
      const trimmedTitle = title.trim()
      if (!trimmedTitle || this.submitting) return
      submitError = ''
      setSubmitting(true)
      try {
        await callbacks.onSubmit({
          title: trimmedTitle,
          description: description.trim() || undefined,
          priority,
          severity,
          screenshot: screenshot ?? undefined,
          assignedTo,
        }, openAfter)
        this.destroy()
      } catch (error) {
        submitError = error instanceof Error
          ? error.message.replace(/^visual-qa:\s*/, '')
          : 'Could not create item. Try again.'
        setSubmitting(false)
      }
    }

    let screenshotExpanded = false

    const renderLightbox = () => {
      const existing = popup.querySelector('.screenshot-lightbox')
      if (existing) existing.remove()
      if (!screenshotExpanded || !screenshot) return
      const lightbox = document.createElement('div')
      lightbox.className = 'screenshot-lightbox'
      lightbox.innerHTML = `
        <div class="screenshot-lightbox-card">
          <div class="screenshot-lightbox-header">
            <span class="screenshot-lightbox-title">Captured image</span>
            <button class="close-btn" aria-label="Close image"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg></button>
          </div>
          <div class="screenshot-lightbox-body">
            <img src="${screenshot}" alt="Screenshot" />
          </div>
        </div>
      `
      lightbox.querySelector('.close-btn')?.addEventListener('click', () => {
        screenshotExpanded = false
        renderLightbox()
      })
      lightbox.addEventListener('click', event => {
        if (event.target === lightbox) {
          screenshotExpanded = false
          renderLightbox()
        }
      })
      popup.appendChild(lightbox)
    }

    const updateScreenshotSlot = () => {
      const slot = popup.querySelector<HTMLElement>('.screenshot-slot')
      if (!slot) return
      if (screenshot) {
        slot.innerHTML = `
          <div class="screenshot-preview">
            <button class="screenshot-expand-btn" aria-label="Enlarge screenshot" type="button">
              <img src="${screenshot}" alt="Screenshot" />
              <span class="screenshot-expand-hint">Click to enlarge</span>
            </button>
            <button class="screenshot-remove" title="Remove screenshot" aria-label="Remove screenshot"><svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg></button>
          </div>`
        slot.querySelector('.screenshot-expand-btn')?.addEventListener('click', () => {
          screenshotExpanded = true
          renderLightbox()
        })
        slot.querySelector('.screenshot-remove')?.addEventListener('click', () => {
          screenshot = null
          screenshotExpanded = false
          renderLightbox()
          updateScreenshotSlot()
        })
      } else {
        slot.innerHTML = `<button class="btn-capture" data-action="capture">${screenshotIcon} Capture screenshot</button>`
        const captureButton = slot.querySelector<HTMLButtonElement>('[data-action="capture"]')!
        captureButton.addEventListener('click', async () => {
          captureButton.textContent = 'Capturing...'
          const dataUrl = await captureElement(pending.element)
          if (!dataUrl) {
            captureButton.textContent = 'Capture failed'
            setTimeout(() => {
              if (captureButton.parentElement) captureButton.innerHTML = `${screenshotIcon} Capture screenshot`
            }, 1200)
            return
          }
          screenshot = dataUrl
          updateScreenshotSlot()
          requestAnimationFrame(() => this.reposition(pending.x, pending.y))
        })
      }
    }

    const assigneeOptions = members.length > 0
      ? `<label class="field-block">
           <span class="field-label">Assignee</span>
           <select data-field="assignedTo">
             <option value=""${assignedTo == null ? ' selected' : ''}>Unassigned</option>
             ${members.map(m => `<option value="${m.staffid}"${assignedTo === m.staffid ? ' selected' : ''}>${esc(m.name || [m.firstname, m.lastname].filter(Boolean).join(' ') || `User ${m.staffid}`)}</option>`).join('')}
           </select>
         </label>`
      : ''

    const render = () => {
      popup.innerHTML = `
        <div class="header">
          <span class="element-tag" title="${esc(pending.elementPath)}">${esc(pending.elementLabel)}</span>
          <button class="close-btn" title="Cancel (Esc)" aria-label="Cancel">${closeIcon}</button>
        </div>
        ${fwBadge}
        ${selectedText}
        <label class="field-block">
          <span class="field-label">Title</span>
          <input class="text-input" data-field="title" placeholder="What needs attention?" value="${esc(title)}" />
        </label>
        <label class="field-block">
          <span class="field-label">Description</span>
          <textarea class="comment-input" data-field="description" placeholder="Add context for developers" rows="4">${esc(description)}</textarea>
        </label>
        <div class="popup-grid">
          <label class="field-block">
            <span class="field-label">Priority</span>
            <select data-field="priority">
              <option value="low"${priority === 'low' ? ' selected' : ''}>Low</option>
              <option value="medium"${priority === 'medium' ? ' selected' : ''}>Medium</option>
              <option value="high"${priority === 'high' ? ' selected' : ''}>High</option>
              <option value="critical"${priority === 'critical' ? ' selected' : ''}>Critical</option>
            </select>
          </label>
          <label class="field-block">
            <span class="field-label">Severity</span>
            <select data-field="severity">
              <option value="minor"${severity === 'minor' ? ' selected' : ''}>Minor</option>
              <option value="major"${severity === 'major' ? ' selected' : ''}>Major</option>
              <option value="critical"${severity === 'critical' ? ' selected' : ''}>Critical</option>
              <option value="blocker"${severity === 'blocker' ? ' selected' : ''}>Blocker</option>
            </select>
          </label>
        </div>
        ${assigneeOptions}
        <div class="screenshot-slot"></div>
        <div class="submit-status" data-role="submit-status" hidden></div>
        <div class="actions">
          <button class="btn-secondary" data-action="cancel">Cancel</button>
          <button class="btn-secondary" data-action="submit-close" ${title.trim() ? '' : 'disabled'}>Create &amp; close</button>
          <button class="btn-primary" data-action="submit-edit" ${title.trim() ? '' : 'disabled'}>Create &amp; edit</button>
        </div>
      `

      const closeButton = popup.querySelector<HTMLButtonElement>('.close-btn')!
      const cancelButton = popup.querySelector<HTMLButtonElement>('[data-action="cancel"]')!
      const submitCloseButton = popup.querySelector<HTMLButtonElement>('[data-action="submit-close"]')!
      const submitEditButton = popup.querySelector<HTMLButtonElement>('[data-action="submit-edit"]')!
      const status = popup.querySelector<HTMLElement>('[data-role="submit-status"]')!
      const titleInput = popup.querySelector<HTMLInputElement>('[data-field="title"]')!
      const descriptionInput = popup.querySelector<HTMLTextAreaElement>('[data-field="description"]')!

      const updateSubmitState = () => {
        const disabled = this.submitting || !title.trim()
        submitCloseButton.disabled = disabled
        submitEditButton.disabled = disabled
      }

      setSubmitting = (active: boolean) => {
        this.submitting = active
        closeButton.disabled = active
        cancelButton.disabled = active
        titleInput.disabled = active
        descriptionInput.disabled = active
        if (assigneeSelect) assigneeSelect.disabled = active
        for (const button of popup.querySelectorAll<HTMLButtonElement>('button')) {
          if (button === submitCloseButton || button === submitEditButton) continue
          button.disabled = active
        }
        submitCloseButton.textContent = active ? 'Creating…' : 'Create & close'
        submitEditButton.textContent = active ? 'Creating…' : 'Create & edit'
        status.hidden = !active && !submitError
        status.classList.toggle('error', !active && !!submitError)
        status.innerHTML = active
          ? '<span class="submit-spinner" aria-hidden="true"></span><span>Creating item…</span>'
          : submitError
            ? `<span>${esc(submitError)}</span>`
            : ''
        updateSubmitState()
      }

      popup.querySelector('.close-btn')?.addEventListener('click', () => {
        if (this.submitting) return
        callbacks.onCancel()
        this.destroy()
      })
      popup.querySelector('[data-action="cancel"]')?.addEventListener('click', () => {
        if (this.submitting) return
        callbacks.onCancel()
        this.destroy()
      })
      submitCloseButton.addEventListener('click', () => { void submit(false) })
      submitEditButton.addEventListener('click', () => { void submit(true) })

      titleInput.addEventListener('input', () => {
        title = titleInput.value
        submitError = ''
        setSubmitting(false)
        updateSubmitState()
      })
      descriptionInput.addEventListener('input', () => {
        description = descriptionInput.value
        submitError = ''
        setSubmitting(false)
      })

      const assigneeSelect = popup.querySelector<HTMLSelectElement>('[data-field="assignedTo"]')
      if (assigneeSelect) {
        assigneeSelect.addEventListener('change', () => {
          const val = assigneeSelect.value
          assignedTo = val ? Number(val) : null
        })
      }

      popup.querySelector<HTMLSelectElement>('[data-field="priority"]')?.addEventListener('change', event => {
        priority = (event.currentTarget as HTMLSelectElement).value as TestingItemPriority
      })
      popup.querySelector<HTMLSelectElement>('[data-field="severity"]')?.addEventListener('change', event => {
        severity = (event.currentTarget as HTMLSelectElement).value as TestingItemSeverity
      })

      for (const input of [titleInput, descriptionInput]) {
        input.addEventListener('keydown', event => {
          const keyEvent = event as KeyboardEvent
          event.stopPropagation()
          if (keyEvent.key === 'Escape') {
            if (this.submitting) return
            if (screenshotExpanded) {
              screenshotExpanded = false
              renderLightbox()
            } else {
              callbacks.onCancel()
              this.destroy()
            }
          }
          if (keyEvent.key === 'Enter' && !keyEvent.shiftKey && event.currentTarget === titleInput) {
            event.preventDefault()
            submit(true)
          }
        })
      }

      updateScreenshotSlot()
      setSubmitting(false)

      titleInput.focus()
      titleInput.setSelectionRange(titleInput.value.length, titleInput.value.length)
    }

    render()

    this.shadow.appendChild(popup)
    ;(document.getElementById('visual-qa-root') ?? document.body).appendChild(this.host)
    this.positionHost(pending.x, pending.y)
    this.setupOutsideClick()
  }

  private stopHostPropagation(host: HTMLElement): void {
    for (const eventName of ['click', 'mousedown', 'pointerdown', 'keydown', 'keyup', 'keypress', 'submit'] as const) {
      host.addEventListener(eventName, event => event.stopPropagation())
    }
  }

  private positionHost(x: number, y: number): void {
    if (!this.host) return
    this.host.setAttribute('popover', 'manual')
    try { this.host.showPopover() } catch {}
    Object.assign(this.host.style, { position: 'fixed', zIndex: '2147483647', left: '-9999px', top: '0' })
    requestAnimationFrame(() => this.reposition(x, y))
  }

  private reposition(x: number, y: number): void {
    if (!this.host) return
    const viewport = window.visualViewport
    const bounds = viewport
      ? { left: viewport.offsetLeft, top: viewport.offsetTop, width: viewport.width, height: viewport.height }
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
    const popup = this.shadow?.querySelector<HTMLElement>('.popup')
    if (!popup) return
    popup.style.maxHeight = `${Math.max(180, bounds.height - 20)}px`
    popup.style.maxWidth = `${Math.max(280, bounds.width - 20)}px`
    const popupRect = popup.getBoundingClientRect()
    const width = popupRect?.width ?? 340
    const height = popupRect?.height ?? 300
    const minLeft = bounds.left + 10
    const maxLeft = bounds.left + bounds.width - width - 10
    const left = Math.max(minLeft, Math.min(x + 10, maxLeft))

    const belowTop = y + 10
    const aboveTop = y - height - 10
    const minTop = bounds.top + 10
    const maxTop = bounds.top + bounds.height - height - 10
    const preferredTop = belowTop + height <= bounds.top + bounds.height - 10 || aboveTop < minTop
      ? belowTop
      : aboveTop
    const top = Math.max(minTop, Math.min(preferredTop, maxTop))
    Object.assign(this.host.style, { left: `${left}px`, top: `${top}px` })
  }

  private boundOutside = (event: MouseEvent): void => {
    if (this.submitting) return
    if (this.host && !this.host.contains(event.target as Node)) this.destroy()
  }

  private setupOutsideClick(): void {
    setTimeout(() => document.addEventListener('mousedown', this.boundOutside), 0)
  }

  destroy(): void {
    this.submitting = false
    this.host?.remove()
    this.host = null
    this.shadow = null
    document.removeEventListener('mousedown', this.boundOutside)
  }
}
